/** Live gateways: the running platform, through the gateway in front of each bundle. */
import { deriveAttention } from '../../domain/attention';
import { historyWindow, shapeHistory, withEstimatedCurrent } from '../../domain/energy';
import type {
  Connection,
  DeviceDetail,
  EnergyHistory,
  HistoryRange,
  ServiceComponent,
  ServiceStatus,
} from '../../domain/model';
import type {
  WireCommand,
  WireDevice,
  WireDeviceStatus,
  WireEnergySeries,
  WireGap,
  WireGeneration,
  WireInventoryItem,
  WireOrder,
  WireTenant,
  WireWebhookDelivery,
  WireWebhookHealth,
} from '../../wire/internal';
import type { WireMeterPoint, WirePublicTenant, WireVisibleMeterPoint } from '../../wire/public';
import {
  GatewayError,
  type Gateways,
  type LabGateway,
  type ManufacturingGateway,
  type OpsGateway,
  type StatusGateway,
  type TenantDevice,
  type TenantGateway,
} from '../types';
import { call } from './http';
import * as map from './mappers';

const zone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

async function energy(base: string, range: HistoryRange): Promise<EnergyHistory> {
  const now = new Date();
  const { from, to, bucket } = historyWindow(range, now);
  const query = new URLSearchParams({ start: from.toISOString(), end: to.toISOString(), bucket, tz: zone() });
  const series = await call<WireEnergySeries>(`${base}/energy?${query}`);
  return shapeHistory(
    range,
    now,
    series.buckets.map((b) => ({ start: b.start, importWh: b.import_wh, exportWh: b.export_wh })),
    {
      peakImport: series.peak_import && { watts: series.peak_import.value, at: series.peak_import.at },
      peakExport: series.peak_export && { watts: series.peak_export.value, at: series.peak_export.at },
      phasePeaks: series.phase_peaks.map((p) => ({ phase: p.phase as 1 | 2 | 3, amps: p.current_ma / 1000, at: p.at })),
    },
  );
}

/** Everything known about one device; `base` is the device's route (differs per surface). */
async function detail(base: string, order: (deviceId: string) => Promise<WireOrder | null>): Promise<DeviceDetail> {
  const [device, status, gaps, generations, commands] = await Promise.all([
    call<WireDevice>(base),
    call<WireDeviceStatus>(`${base}/status`),
    call<{ gaps: WireGap[] }>(`${base}/gaps`),
    call<{ generations: WireGeneration[] }>(`${base}/generations`),
    call<{ commands: WireCommand[] }>(`${base}/commands`),
  ]);
  return map.deviceDetail(
    device,
    status,
    gaps.gaps,
    generations.generations,
    commands.commands ?? [],
    await order(device.id),
    new Date(),
  );
}

function lab(connection: Connection | null): LabGateway {
  return {
    mode: 'live',
    async device() {
      // /lab is bound to one device by the gateway; the lab app has no order context.
      const d = await detail('/lab', async () => null);
      const reading =
        d.reading && connection ? { ...d.reading, phases: withEstimatedCurrent(d.reading, connection) } : d.reading;
      return { ...d, reading, connection };
    },
    history: (range) => energy('/lab', range),
  };
}

function ops(): OpsGateway {
  const orders = async () => (await call<{ orders: WireOrder[] }>('/internal/v1/orders')).orders;
  const tenants = async () => (await call<{ tenants: WireTenant[] }>('/internal/v1/tenants')).tenants;
  const orderFor = async (deviceId: string) => {
    const match = (await orders()).find((o) => o.device_id === deviceId);
    return match ? call<WireOrder>(`/internal/v1/orders/${match.id}`) : null;
  };
  const gateway: OpsGateway = {
    mode: 'live',
    async health() {
      // /readyz answers 503 with the same body when a dependency is down.
      const ready = await fetch('/readyz')
        .then((r) => r.json() as Promise<{ database: boolean; mqtt: boolean; version: string }>)
        .catch(() => null);
      return {
        api: ready !== null,
        database: ready?.database ?? null,
        ingestion: ready?.mqtt ?? null,
        version: ready?.version ?? null,
      };
    },
    async devices() {
      const now = new Date();
      return (await call<{ devices: WireDevice[] }>('/internal/v1/devices')).devices.map((d) =>
        map.deviceSummary(d, now),
      );
    },
    device: (id) => detail(`/internal/v1/devices/${encodeURIComponent(id)}`, orderFor),
    history: (id, range) => energy(`/internal/v1/devices/${encodeURIComponent(id)}`, range),
    commandsUnavailable: null,
    async sendCommand(id, type) {
      await call(`/internal/v1/devices/${encodeURIComponent(id)}/commands`, { method: 'POST', body: { type } });
    },
    organisations: async () => (await tenants()).map(map.organisation),
    deliveries: async (id) =>
      (
        await call<{ deliveries: WireWebhookDelivery[] }>(
          `/internal/v1/tenants/${encodeURIComponent(id)}/webhooks/deliveries`,
        )
      ).deliveries.map(map.webhookDelivery),
    orders: async () => (await orders()).map(map.orderSummary),
    order: async (id) => map.orderSummary(await call<WireOrder>(`/internal/v1/orders/${encodeURIComponent(id)}`)),
    stock: async () =>
      (await call<{ devices: WireInventoryItem[] }>('/internal/v1/inventory')).devices.map(map.stockItem),
    async attention() {
      const now = new Date();
      const [health, deviceList, orderList, organisations] = await Promise.all([
        gateway.health(),
        call<{ devices: WireDevice[] }>('/internal/v1/devices'),
        gateway.orders(),
        gateway.organisations(),
      ]);
      // Gaps live per device; only activated devices can have them.
      const devices = await Promise.all(
        deviceList.devices.map(async (d) => {
          const summary = map.deviceSummary(d, now);
          const openGaps =
            d.activation_state === 'activated'
              ? (await call<{ gaps: WireGap[] }>(`/internal/v1/devices/${d.id}/gaps`)).gaps.map((g) => ({
                  fromSeq: g.from_seq,
                  toSeq: g.to_seq,
                  detectedAt: g.detected_at,
                }))
              : [];
          return { ...summary, openGaps, generationNo: d.generation_no };
        }),
      );
      return deriveAttention({ now, devices, orders: orderList, organisations, health });
    },
    assignDevice: async (orderId, serial) =>
      map.orderSummary(
        await call<WireOrder>(`/internal/v1/orders/${encodeURIComponent(orderId)}/assign-device`, {
          method: 'POST',
          body: { serial_number: serial },
        }),
      ),
    advanceShipment: async (orderId, step) =>
      map.orderSummary(
        await call<WireOrder>(`/internal/v1/orders/${encodeURIComponent(orderId)}/shipment`, {
          method: 'POST',
          body: { status: step },
        }),
      ),
  };
  return gateway;
}

function tenant(): TenantGateway {
  const toDevice = (mp: WireMeterPoint, now: Date): TenantDevice | null => {
    const installation = mp.installation ?? mp.installations?.at(-1);
    const d = installation?.device;
    if (!installation || !d) return null;
    const data = map.dataStateOf(mp.latest, null, now);
    return {
      id: d.id,
      serial: d.serial_number,
      lifecycle: map.lifecycleOf(
        {
          status: 'active',
          activation_state: d.activation_state,
          inventory_state: d.inventory_state,
          online: d.online,
        },
        data,
      ),
      data,
      online: d.online,
      lastSeenAt: d.last_seen_at,
      firmware: d.software_version,
      hardware: d.hardware_revision,
      model: map.modelOf(d.device_type),
      organisation: null,
      customerRef: mp.customer?.external_id ?? null,
      link: { kind: 'unknown' },
      simulated: map.isSimulated(d.device_type),
      customer:
        mp.customer && mp.property
          ? { name: mp.customer.name, address: mp.property.address, city: mp.property.city }
          : null,
      lastDataAt: mp.latest?.received_at ?? null,
      netW: mp.latest ? (mp.latest.power_import_w ?? 0) - (mp.latest.power_export_w ?? 0) : null,
      installationNo: installation.installation_no,
      meterPointId: mp.id,
      orderIds: mp.order_ids ?? [],
    };
  };
  const devices = async () => {
    const now = new Date();
    const visible = (await call<{ meter_points: WireVisibleMeterPoint[] }>('/api/v1/meter-points')).meter_points.filter(
      (m) => m.Access === 'owner',
    );
    // One call per meter point: fine for a first fleet, replaced by a portal list route before fleets grow.
    const points = await Promise.all(
      visible.map((m) => call<WireMeterPoint>(`/api/v1/meter-points/${m.MeterPointID}`)),
    );
    return points.map((mp) => toDevice(mp, now)).filter((d): d is TenantDevice => d !== null);
  };
  return {
    mode: 'live',
    async organisation() {
      const t = await call<WirePublicTenant>('/api/v1/tenant');
      return {
        id: t.id,
        name: t.name,
        slug: t.slug,
        credential: t.principal.kind === 'client' ? `Machine client ${t.principal.client_id ?? ''}`.trim() : 'API key',
        scopes: t.principal.scopes,
      };
    },
    devices,
    async device(id) {
      const found = (await devices()).find((d) => d.id === id);
      if (!found)
        throw new GatewayError(
          'This device does not belong to your organisation.',
          404,
          'not_found',
          `GET device ${id}`,
        );
      return found;
    },
    orders: async () => (await call<{ orders: WireOrder[] }>('/api/v1/orders')).orders.map(map.orderSummary),
    order: async (id) => map.orderSummary(await call<WireOrder>(`/api/v1/orders/${encodeURIComponent(id)}`)),
    webhook: async () => map.webhookHealth(await call<WireWebhookHealth>('/api/v1/webhooks/endpoint')),
    deliveries: async () =>
      (await call<{ deliveries: WireWebhookDelivery[] }>('/api/v1/webhooks/deliveries?limit=25')).deliveries.map(
        map.webhookDelivery,
      ),
    clients: async () => ({
      available: false,
      reason:
        'Machine clients are created and rotated by P1Plug operations for now. Self-service arrives with portal sign-in.',
    }),
    requestLog: async () => ({
      available: false,
      reason:
        'The platform does not record a per-request log for customers yet. Webhook deliveries below are recorded and real.',
    }),
  };
}

const manufacturing: ManufacturingGateway = {
  mode: 'live',
  connected: {
    available: false,
    reason:
      'No manufacturing backend is connected: batches, test stations and QA results are not recorded by the platform yet.',
  },
  batches: async () => [],
  units: async () => [],
  unit: async () => null,
  test: async (serial) => {
    throw new GatewayError('No test station is connected.', 501, 'not_connected', `test ${serial}`);
  },
};

const COMPONENTS: Pick<ServiceComponent, 'key' | 'name' | 'description'>[] = [
  {
    key: 'connectivity',
    name: 'Device connectivity',
    description: 'P1Plugs reaching the platform over the mobile network',
  },
  { key: 'ingestion', name: 'Meter data ingestion', description: 'Receiving and storing smart-meter readings' },
  { key: 'api', name: 'Customer API', description: 'Orders, meter points, devices and telemetry' },
  { key: 'webhooks', name: 'Webhooks', description: 'Event delivery to customer endpoints' },
  { key: 'portal', name: 'Customer portal', description: 'portal.p1plug.nl' },
  { key: 'developers', name: 'Developer platform', description: 'Documentation and sandbox' },
  { key: 'ordering', name: 'Ordering and fulfilment API', description: 'Order intake and shipping' },
];

const status: StatusGateway = {
  mode: 'live',
  async status(): Promise<ServiceStatus> {
    const ready = await fetch('/readyz')
      .then((r) => r.json() as Promise<{ database: boolean; mqtt: boolean }>)
      .catch(() => null);
    // Only what is actually probed gets a state. Everything else says "not monitored yet".
    const probed: Record<string, ServiceComponent['state']> = {
      api: ready === null ? 'major_outage' : ready.database ? 'operational' : 'major_outage',
      ordering: ready === null ? 'major_outage' : ready.database ? 'operational' : 'major_outage',
      ingestion: ready === null ? 'major_outage' : ready.mqtt && ready.database ? 'operational' : 'major_outage',
    };
    return {
      checkedAt: new Date().toISOString(),
      components: COMPONENTS.map((c) => ({ ...c, state: probed[c.key] ?? 'not_monitored', history: [] })),
      incidents: [],
      maintenance: [],
      incidentHistoryAvailable: false,
    };
  },
};

export function liveGateways(options: { connection: Connection | null }): Gateways {
  return { mode: 'live', lab: lab(options.connection), ops: ops(), tenant: tenant(), manufacturing, status };
}
